import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DriverDriveTimesDto, LatestDriveTimesDto } from "../../models/driveTime/driverDriveTimes";
import { getLatestDriveTimeInfoByEmployee } from "../../services/driveTimesService";
import { Box, CircularProgress, Typography } from "@mui/material";
import AccordionGroup from "../../framework/muiAccordion";

interface IDrivingTimesSimpleDisplay {
    employeeId: string;
}

export const DrivingTimeSimpleDisplay = (props: IDrivingTimesSimpleDisplay) => {
    const [loading, setLoading] = useState(false);
    const [driveTimeData, setDriveTimeData] = useState<LatestDriveTimesDto|null>(null);
    const {
        employeeId
    } = props;

    const { t } = useTranslation();

    useEffect(() => {
        if(employeeId) {
            setLoading(true);
            getLatestDriveTimeInfoByEmployee(employeeId)
                .then((response) => {
                    setDriveTimeData(response.data);
                    setLoading(false);
                }
                ).catch((error) => {
                    console.error(`Error fetching drive time data for employee ${employeeId}. Error message:`, error);
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, [employeeId]);

    const getSectionContent = (driveTimeData : DriverDriveTimesDto) => {
        const accordionContent: JSX.Element[] = [];
        accordionContent.push(
            <AccordionDetailsDriveTimeItem key="todayDrivingTime" title={t("driveTimes.todayDrivingTime")} value={formatDriveTimeHours(driveTimeData.todayDrivingTime)} />,
            <AccordionDetailsDriveTimeItem key="currentFixedWeekDriveTime" title={t("driveTimes.currentFixedWeekDriveTime")} value={formatDriveTimeHours(driveTimeData.currentFixedWeekDriveTime)} />,
            <AccordionDetailsDriveTimeItem key="pastFixedWeekDriveTime" title={t("driveTimes.pastFixedWeekDriveTime")} value={formatDriveTimeHours(driveTimeData.pastFixedWeekDriveTime)} />,
            <AccordionDetailsDriveTimeItem key="past7Days" title={t("driveTimes.past7Days")} value={formatDriveTimeHours(driveTimeData.past7Days)} />,
            <AccordionDetailsDriveTimeItem key="past14Days" title={t("driveTimes.past14Days")} value={formatDriveTimeHours(driveTimeData.past14Days)} />,
            <AccordionDetailsSubTitle key="availableDriveTimesTitle" title={t("driveTimes.availableDriveTimes")} />,
            <AccordionDetailsDriveTimeItem key="currentFixedWeekDriveTimeAvailable" title={t("driveTimes.currentFixedWeekDriveTimeAvailable")} value={formatDriveTimeHours(driveTimeData.currentFixedWeekDriveTimeAvailable)} />,
            <AccordionDetailsDriveTimeItem key="nextFixedWeekDriveTimeAvailable" title={t("driveTimes.nextFixedWeekDriveTimeAvailable")} value={formatDriveTimeHours(driveTimeData.nextFixedWeekDriveTimeAvailable)} />
        );
        return accordionContent;
    };

    const formatDriveTimeHours = (hours?: number): string => {
        return hours ? (hours / 60).toFixed(2).toString() : "0:00";
    };

    return (
        <Box sx={{ p: 1 }}>
            {loading ? (
                <CircularProgress />
            ) : (
                <>
                    <AccordionGroup
                        tooltip={t("driveTimes.workTimeBasedDrivingTimeTooltip")}
                        items={[
                            driveTimeData && driveTimeData.fromWorkTime
                                ? {
                                    header: t("driveTimes.title"),
                                    subheader: t("driveTimes.fromWorkTime"),
                                    content: getSectionContent(
                                        driveTimeData.fromWorkTime
                                    ),
                                }
                                : null,
                        ].filter(Boolean)}
                    />
                </>
            )
            }
        </Box>
    );
};


interface AccordionDetailsDriveTimeItemProps {
    title: string;
    value: string;
}
const AccordionDetailsDriveTimeItem = ({
    title,
    value,
}: AccordionDetailsDriveTimeItemProps) => {
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
            }}
        >
            <Box
                sx={{
                    textAlign: "left",
                    pt: 1,
                }}
            >
                <Typography sx={{ color: "primary.dark" }}>{title}</Typography>
            </Box>
            <Box
                sx={{
                    pt: 1,
                    textAlign: "right",
                    whiteSpace: "nowrap",
                }}
            >
                <Typography sx={{ color: "success.main" }}>{value}</Typography>
            </Box>
        </Box>
    );
};


interface AccordionDetailsSubTitleProps {
    title: string;
}
const AccordionDetailsSubTitle = ({
    title,
}: AccordionDetailsSubTitleProps) => {
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
            }}
        >
            <Box
                sx={{
                    textAlign: "left",
                    pt: 1,
                    pb: 0,
                    width: "100%",
                    borderBottom: "1px",
                    borderColor: "primary.main",
                    mb: 0,
                }}
            >
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: "bold",
                        color: "primary.main",
                    }}
                >
                    {title}
                </Typography>
            </Box>
        </Box>
    );
};